// ACT1: what the visitor does while they read, as SEE1's story events (ORDER-SEE1 VALUES (1)), the batcher that posts
// them to POST /api/visit/:id/story, and the rules for the one slot the reply may fill (the docked ask, comp B, hub pick
// 2026-10-01 02:57) — pure logic with no DOM: every time is passed in. page.ts wires it to the page (IntersectionObserver,
// visibility, scroll, taps, the ask's words field, fetch); tests/act1.test.ts drives it with a fake clock.
export type Part = "hero" | "ask" | "faq" | "doors" | `section:${string}`;
export type MoveEvent =
  | { t: "read"; part: Part; s: number }
  | { t: "seen"; part: Part; s: number }
  | { t: "reread"; part: Part }
  | { t: "away"; s: number }
  | { t: "scroll"; speed: "fast" }
  | { t: "tap"; part: Part }
  | { t: "type"; words: string }
  | { t: "dnt" };
export type Piece = { id: string; role: string; text: string; href?: string };

export const READ_MS = 2000; // a part counts as read once it leaves view after 2 s at least half on screen, tab visible
export const SEEN_MS = 500; // SEE1: on screen 0.5 s up to READ_MS, never read, = seen (passed over, not stopped at)
export const REREAD_MS = 2000; // a part already read that comes back for 2 s
export const AWAY_MS = 5000; // the tab hidden 5 s or more, then back
export const FAST_SCREENS = 1.5; // screen heights per second, held for FAST_MS, at most once per FAST_GAP_MS
export const FAST_MS = 1000;
export const FAST_GAP_MS = 10000;
export const POST_GAP_MS = 3000; // at most one story post per 3 s
export const POST_MAX = 50; // at most 50 events per post
export const CHANGE_GAP_MS = 10000; // the slot changes at most once per 10 s
export const CHANGES_MAX = 4; // and at most four times per visit
export const PIECE_MAX = 110; // the brief's DENSITY BUDGET: a longer piece is refused, never clamped
export const WORDS_MAX = 200;

const secs = (ms: number): number => Math.min(600, Math.max(1, Math.round(ms / 1000)));

// navigator.doNotTrack "1" or globalPrivacyControl true: the page sends {"t":"dnt"} once and nothing else, ever.
export function dntOn(nav: { doNotTrack?: string | null; globalPrivacyControl?: boolean } | null | undefined): boolean {
  return !!nav && (nav.doNotTrack === "1" || nav.globalPrivacyControl === true);
}

// One stretch of a part on screen: when it (or the tab) last became visible, the visible ms banked before that, and
// whether this stretch has already said reread.
type Stretch = { since: number; acc: number; reread: boolean };

export class Reader {
  private emit: (e: MoveEvent) => void;
  private on = new Map<Part, Stretch>();
  private read = new Set<Part>();
  private visible: boolean;
  private hiddenAt: number;
  private samples: { t: number; y: number }[] = [];
  private fastAt = -Infinity;

  constructor(emit: (e: MoveEvent) => void, now: number, visible = true) {
    this.emit = emit;
    this.visible = visible;
    this.hiddenAt = now;
  }

  private seen(s: Stretch, now: number): number {
    return s.acc + (this.visible ? now - s.since : 0);
  }

  private check(part: Part, s: Stretch, now: number): void {
    if (!s.reread && this.read.has(part) && this.seen(s, now) >= REREAD_MS) {
      s.reread = true;
      this.emit({ t: "reread", part });
    }
  }

  // A part's "at least half on screen" state changed (page.ts decides what half means for a part taller than the screen).
  view(part: Part, onScreen: boolean, now: number): void {
    const s = this.on.get(part);
    if (onScreen) {
      if (!s) this.on.set(part, { since: now, acc: 0, reread: false });
      return;
    }
    if (!s) return;
    this.check(part, s, now);
    this.on.delete(part);
    const ms = this.seen(s, now);
    if (ms >= READ_MS) {
      this.read.add(part);
      this.emit({ t: "read", part, s: secs(ms) });
    } else if (ms >= SEEN_MS && !this.read.has(part)) {
      this.emit({ t: "seen", part, s: secs(ms) });
    }
  }

  // Says any reread now due; nextAt is when the next one can fall due (null: none can while nothing changes).
  tick(now: number): void {
    for (const [part, s] of this.on) this.check(part, s, now);
  }

  nextAt(): number | null {
    if (!this.visible) return null;
    let at: number | null = null;
    for (const [part, s] of this.on) {
      if (s.reread || !this.read.has(part)) continue;
      const due = s.since + Math.max(0, REREAD_MS - s.acc);
      if (at === null || due < at) at = due;
    }
    return at;
  }

  hide(now: number): void {
    if (!this.visible) return;
    this.tick(now);
    for (const s of this.on.values()) s.acc += now - s.since;
    this.visible = false;
    this.hiddenAt = now;
  }

  show(now: number): void {
    if (this.visible) return;
    this.visible = true;
    for (const s of this.on.values()) s.since = now;
    const ms = now - this.hiddenAt;
    if (ms >= AWAY_MS) this.emit({ t: "away", s: secs(ms) });
  }

  // The page's scroll position (y) and screen height. Fast = more than 1.5 screens per second over the last second of
  // unbroken scrolling (no gap over 300 ms between scroll events), said at most once per 10 s.
  scroll(y: number, screen: number, now: number): void {
    const last = this.samples[this.samples.length - 1];
    if (last && now - last.t > 300) this.samples = [];
    this.samples.push({ t: now, y });
    while (this.samples.length > 1 && this.samples[1].t <= now - FAST_MS) this.samples.shift();
    const a = this.samples[0];
    const span = now - a.t;
    if (span < FAST_MS || screen <= 0) return;
    if (Math.abs(y - a.y) / screen / (span / 1000) > FAST_SCREENS && now - this.fastAt >= FAST_GAP_MS) {
      this.fastAt = now;
      this.emit({ t: "scroll", speed: "fast" });
    }
  }

  tap(part: Part): void {
    this.emit({ t: "tap", part });
  }

  // The ask's own words field, on blur; empty words say nothing.
  typed(words: string): void {
    const w = words.trim().slice(0, WORDS_MAX);
    if (w) this.emit({ t: "type", words: w });
  }
}

// Queues events and hands out at most one post per 3 s, at most 50 events each, seq counting up from the visit's last.
// Under do-not-track it hands out exactly one post, [{"t":"dnt"}], and drops every event pushed.
export class Batcher {
  private queue: MoveEvent[] = [];
  private seq: number;
  private last = -Infinity;
  private dnt: boolean;
  private dntSent = false;

  constructor(opts: { dnt: boolean; seq?: number }) {
    this.dnt = opts.dnt;
    this.seq = opts.seq ?? 0;
  }

  push(e: MoveEvent): void {
    if (!this.dnt) this.queue.push(e);
  }

  private pending(): boolean {
    return this.dnt ? !this.dntSent : this.queue.length > 0;
  }

  // When the next post may go (-Infinity = now), or null when there is nothing to send.
  nextAt(): number | null {
    return this.pending() ? this.last + POST_GAP_MS : null;
  }

  take(now: number): { events: MoveEvent[]; seq: number } | null {
    if (!this.pending() || now - this.last < POST_GAP_MS) return null;
    this.last = now;
    this.seq += 1;
    if (this.dnt) {
      this.dntSent = true;
      return { events: [{ t: "dnt" }], seq: this.seq };
    }
    return { events: this.queue.splice(0, POST_MAX), seq: this.seq };
  }
}

// A reply's show, only in its exact shape: id, role and text strings (text 1–110 characters, never clamped), href only a
// tel:, mailto:, http(s) or same-page address. Anything else is no piece.
const HREF = /^(?:tel:\+?[0-9]{7,15}|mailto:[^\s<>"]+|https?:\/\/[^\s<>"]+|#[A-Za-z0-9_-]+|\/[^\s<>"]*)$/;
export function pieceOf(x: unknown): Piece | null {
  if (!x || typeof x !== "object") return null;
  const o = x as Record<string, unknown>;
  if (typeof o.id !== "string" || !o.id || typeof o.role !== "string" || typeof o.text !== "string") return null;
  const text = o.text.trim();
  if (!text || text.length > PIECE_MAX) return null;
  if (o.href !== undefined && o.href !== null && (typeof o.href !== "string" || !HREF.test(o.href))) return null;
  return typeof o.href === "string" ? { id: o.id, role: o.role, text, href: o.href } : { id: o.id, role: o.role, text };
}

// How the slot draws a piece: a link to its href; a button that opens the page's own ask for an ask or later piece
// without one; else the line alone.
export function actOf(p: Piece): "link" | "ask" | "line" {
  if (p.href) return "link";
  return p.role === "ask" || p.role === "later" ? "ask" : "line";
}

// The slot's rules. Only the newest reply counts (by its post's seq; an older reply arriving late is ignored); a show
// applies nothing while a form field has focus or text (busy), after a call or book tap or a sent form (stop), within
// 10 s of the last change, or past four changes in the visit; the piece already showing is no change.
export class SlotRules {
  private newest = 0;
  private last = -Infinity;
  private changes: number;
  private shown: string | null = null;
  private done = false;

  constructor(changes = 0) {
    this.changes = changes;
  }

  stop(): void {
    this.done = true;
  }

  count(): number {
    return this.changes;
  }

  offer(seq: number, show: unknown, now: number, busy: boolean): Piece | null {
    if (seq <= this.newest) return null;
    this.newest = seq;
    const p = pieceOf(show);
    if (!p || this.done || busy || p.id === this.shown) return null;
    if (now - this.last < CHANGE_GAP_MS || this.changes >= CHANGES_MAX) return null;
    this.last = now;
    this.changes += 1;
    this.shown = p.id;
    return p;
  }
}
